import React, { useState, useMemo } from "react";
import PersistentTabs from "@/common/ui/PersistentTabs";
import { motion, AnimatePresence } from "framer-motion";
import EmptyList from "@/components/common/EmptyList";
import { Center } from "@/common/css";
import { useImportDetails } from "@/services/employee.services";
import SimpleTableView from "@/components/common/table/SimpleTableView";
import { ColumnDef } from "@tanstack/react-table";
import { LightText2 } from "@/common/ui/Text";

interface ImportSummaryProps {
  isOpen: boolean;
  onClose: () => void;
  importId: string;
  defaultTab?: "Added" | "Updated" | "Skipped";
}

const ImportSummary: React.FC<ImportSummaryProps> = ({ importId, defaultTab }) => {
  const [activeTab, setActiveTab] = useState<"Added" | "Updated" | "Skipped">(defaultTab || "Added");
  const [localTabData, setLocalTabData] = useState({ Added: { count: 0 }, Updated: { count: 0 }, Skipped: { count: 0 } });
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);

  const { data, isLoading, isError } = useImportDetails(importId, currentPage, activeTab, perPage);

  const skipRecordsErrorsLink = data?.skipRecordsErrorsLink;

  const tabs = [
    { key: "Added", label: "Added" },
    { key: "Updated", label: "Updated" },
    { key: "Skipped", label: "Skipped" },
  ];

  // Get current tab's data and columns from API response
  const tabSummary = React.useMemo(() => data?.summary?.find((item: any) => item.status === activeTab) || {}, [data?.summary, activeTab]);
  const tabData = React.useMemo(() => tabSummary.values || [], [tabSummary]);
  const tabColumnHeaders = React.useMemo(() => tabSummary.columnHeaders || [], [tabSummary]);

  // Update localTabData only when not loading
  React.useEffect(() => {
    if (!isLoading && data?.summary) {
      setLocalTabData({
        Added: { count: data?.summary?.find((item: any) => item.status === "Added")?.count || 0 },
        Updated: { count: data?.summary?.find((item: any) => item.status === "Updated")?.count || 0 },
        Skipped: { count: data?.summary?.find((item: any) => item.status === "Skipped")?.count || 0 },
      });
    }
  }, [isLoading, data?.summary]);

  // Create dynamic columns for current tab
  const dynamicColumns = useMemo<ColumnDef<any>[]>(() => {
    if (!tabColumnHeaders.length) {
      return [];
    }
    return tabColumnHeaders.map((col: any) => ({
      id: col.id,
      header: col.name,
      accessorKey: col.id,
      cell: ({ row }: any) => <div className="text-sm text-text-default">{row.original[col.id] || "-"}</div>,
    }));
  }, [tabColumnHeaders]);

  // Transform data to ensure each item has an id
  const transformedData = useMemo(() => {
    return tabData.map((item: any, index: number) => ({
      ...item,
      id: item.id?.toString() || `row-${index}`,
    }));
  }, [tabData]);
  console.log({ tabs });
  return (
    <div className="flex flex-col h-full">
      {/* Tabs Header */}
      <div className="flex justify-between items-center border-b border-gray-200 px-6">
        <PersistentTabs
          tabs={tabs}
          initialTab={activeTab}
          tabData={localTabData}
          isLoading={isLoading}
          onTabChange={(key) => {
            setActiveTab(key as "Added" | "Updated" | "Skipped");
            setCurrentPage(1);
          }}
        />
        {activeTab === "Skipped" && skipRecordsErrorsLink && (
          <a href={skipRecordsErrorsLink} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">
            Download error report
          </a>
        )}
      </div>

      {/* Tab Content */}
      <div className=" h-full">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="overflow-auto h-full"
          >
            {isError ? (
              <Center className="h-full min-h-[300px]">
                <LightText2 text="Error loading import summary" />
              </Center>
            ) : tabData.length === 0 && !isLoading ? (
              <Center className="h-full min-h-[300px] ">
                <EmptyList text="Oops! no record found" icon="/icons/empty-inbox.svg" className="gap-4" />
              </Center>
            ) : dynamicColumns.length > 0 ? (
              <SimpleTableView
                data={transformedData}
                columnsConfig={dynamicColumns}
                className="p-3"
                columnClassName="max-w-[150px] w-[150px] min-w-[100px]"
                tableClassName="!h-[calc(100dvh-168px)] overflow-auto"
                isLoading={isLoading}
                externalPagination={{
                  currentPage: currentPage,
                  totalPages: data?.totalPage || 1,
                  onPageChange: (page: number) => setCurrentPage(page),
                  perPage: { value: perPage, label: `Records per page ${perPage}` },
                  setPerPage: (count) => {
                    setPerPage(count.value);
                    setCurrentPage(1);
                  },
                }}
              />
            ) : isLoading ? (
              <Center className="h-full min-h-[300px] text-13 !leading-normal">
                <LightText2 text="Loading..." />
              </Center>
            ) : (
              <div className="p-6 space-y-4">
                {tabData.map((item: any, index: number) => (
                  <div key={index} className="p-4 border border-gray-200 rounded-lg">
                    <pre className="text-sm text-gray-600">{JSON.stringify(item, null, 2)}</pre>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ImportSummary;
